import { HttpErrorResponse } from '@angular/common/http';

/**
 * Helpers that keep sensitive data out of log output and error reports (BSI TR-03161-2 O.Source_3).
 *
 * Several backend calls carry a token in the address (/emergency-agent-token/{token}, /phone-access-token/{token},
 * /wifi-setting-token/is-valid/{token}, /eaf/{token}), so an HttpErrorResponse (its url and message contain that
 * address) and every request breadcrumb would otherwise copy the token into the console and into Sentry.
 */

const REDACTED = '[redacted]';
const SEGMENT = `[^/?#\\s"'\`]+`;
const PATH_TOKENS = new RegExp(`(emergency-agent-token|phone-access-token|wifi-setting-token/is-valid|wifi-setting-token|/is-valid|/eaf|/pa)/${SEGMENT}`, 'g');
const QUERY_TOKENS = /([?&#](?:token|eaa|eaf|wst|access_token|id_token|refresh_token|code|state|nonce|code_verifier)=)[^&#\s"'`]*/gi;
const BEARER = /(Bearer\s+)[A-Za-z0-9._~+/=-]+/gi;
const JWT = /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]*/g;

/** Replaces tokens in addresses, bearer headers and JWTs inside a piece of text. */
export function scrubSensitiveText(text: string): string {
    return text
        .replace(PATH_TOKENS, `$1/${REDACTED}`)
        .replace(QUERY_TOKENS, `$1${REDACTED}`)
        .replace(BEARER, `$1${REDACTED}`)
        .replace(JWT, REDACTED);
}

/** Scrubs every string inside an object tree (used for the events and breadcrumbs sent to Sentry). Changes the object in place. */
export function scrubSensitiveData<T>(value: T, depth = 0, seen = new WeakSet<object>()): T {
    if (typeof value === 'string') {
        return scrubSensitiveText(value) as unknown as T;
    }
    if (value === null || typeof value !== 'object' || depth > 12 || seen.has(value as object)) {
        return value;
    }
    seen.add(value as object);
    const record = value as Record<string, unknown>;
    for (const key of Object.keys(record)) {
        const entry = record[key];
        if (typeof entry === 'string' || (entry !== null && typeof entry === 'object')) {
            try {
                record[key] = scrubSensitiveData(entry, depth + 1, seen);
            } catch {
                // read-only property: leave it as it is
            }
        }
    }
    return value;
}

/**
 * A short description of an error that is safe to log: for a failed HTTP call only the status (not the address,
 * which can contain a token, and not the response body); for anything else the error's name.
 */
export function safeErrorText(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
        return `HTTP ${error.status}${error.statusText ? ' ' + error.statusText : ''}`;
    }
    if (error instanceof Error) {
        return scrubSensitiveText(error.name + (error.message ? ': ' + error.message : ''));
    }
    return typeof error === 'string' ? scrubSensitiveText(error) : 'unknown error';
}
